import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuftragCtx } from './AuftragLayout.jsx';
import { DokumentListe, TeamWahl, Abschnittstitel, useOnline, TitelFeld, ExcelKnopf } from './Checkliste.jsx';
import { useChecklisten, useMe, usePlaene } from '../hooks/data.js';
import { useAbschnitte, useExcelEntwurf } from '../hooks/useExcelEntwurf.js';
import { AbschnittsLeiste, SpeichernLeiste } from '../components/AbschnittsLeiste.jsx';
import { useToast } from '../state/toast.jsx';
import { api } from '../lib/api.js';
import { isoDate } from '../lib/format.js';
import { Icon } from '../components/Icon.jsx';
import { Stand } from '../components/Stand.jsx';

/** Wie api/src/lib/bewaesserungsplan.js */
const PROGRAMME = ['A', 'B', 'C'];
const TAGE = ['mo', 'di', 'mi', 'do', 'fr', 'sa', 'so'];
const TAG_NAMEN = { mo: 'Mo', di: 'Di', mi: 'Mi', do: 'Do', fr: 'Fr', sa: 'Sa', so: 'So' };
const MAX_ZONEN = 40;
const LAUFZEITEN = ['5 min', '10 min', '15 min', '20 min', '25 min', '30 min', '40 min', '45 min', '1 h', '1,5 h', '2 h'];
const STEUERGERAETE = ['Hunter X2', 'Hunter Pro-C', 'Hunter HCC', 'Hunter Hydrawise', 'Rain Bird ESP-TM2'];
const leeresProgramm = () => ({ tage: Object.fromEntries(TAGE.map((t) => [t, false])), modus: 'tage', intervall: '', start: ['', '', '', ''] });
const leereZone = (nr, bezeichnung = '') => ({ nr, bezeichnung, laufzeit: { A: '', B: '', C: '' } });
const leererPlan = (titel, ausgefuelltVon) => ({
  version: 1, titel, ausgefuelltVon, datum: isoDate(),
  programme: Object.fromEntries(PROGRAMME.map((p) => [p, leeresProgramm()])),
  zonen: [], steuergeraet: { typ: '', standort: '' }, regensensor: '', sonstiges: '',
});

const tageText = (p) => {
  if (p.modus === 'gerade') return 'Gerade Tage';
  if (p.modus === 'ungerade') return 'Ungerade Tage';
  if (p.modus === 'intervall') return `Intervall${p.intervall ? ` ${p.intervall}` : ''}`;
  const an = TAGE.filter((t) => p.tage[t]);
  return an.length === 7 ? 'Täglich' : an.map((t) => TAG_NAMEN[t]).join(' ');
};
const programmInfo = (p) => [tageText(p), p.start.filter(Boolean).join(', ')].filter(Boolean).join(' · ');

/** Alle Bewässerungspläne des Auftrags – öffnen oder neu anlegen. */
export function Plaene() {
  const { nr } = useAuftragCtx();
  return <DokumentListe q={usePlaene(nr)} pfad="plaene" entwurfPrefix="plan" leer="Für diesen Auftrag gibt es noch keinen Bewässerungsplan."
    neu="Neuer Bewässerungsplan" hinweis="Pläne gehören zur Anlage: Sie liegen als Excel in den Dateien des Kunden und erscheinen bei jedem Auftrag dieses Kunden – z. B. einer pro Steuergerät." icon="clock" />;
}

export function Bewaesserungsplan() {
  const { dateiId } = useParams();
  const { base } = useAuftragCtx();
  if (dateiId !== 'neu' && !/^\d{1,20}$/.test(dateiId ?? '')) return <Navigate to={`${base}/plaene`} replace />;
  return <PlanBearbeiten key={dateiId} dateiId={dateiId} />;
}

/**
 * Bewässerungszeitplan: Programme A/B/C (Tage, Startzeiten), Laufzeit pro Zone und Programm, Steuergerät.
 * Wenige Tipps: Programm wählen → Tage (oder «Täglich») → Startzeit → «Für alle Zonen» Laufzeit. Zonen lassen sich
 * aus einer Übersichtsliste übernehmen. Gespeichert als Excel in den Exxas-Dateien des KUNDEN (wird beim Speichern ersetzt).
 */
function PlanBearbeiten({ dateiId }) {
  const { nr, base, auftrag } = useAuftragCtx();
  const me = useMe();
  const toast = useToast();
  const online = useOnline();
  const teamName = (team) => team.find((t) => (me.data?.name ?? '').toLowerCase().startsWith(t.toLowerCase())) || me.data?.name || '';
  const { neu, q, daten, server, entwurf, veraltet, team, aendern, verwerfen, absenden, fehler, setFehler, speichert, vorgemerkt } = useExcelEntwurf({
    art: 'plaene', nr, dateiId, base, pfad: 'plaene', entwurfPrefix: 'plan',
    leer: (liste) => leererPlan(auftrag?.kunde?.firma ?? '', teamName(liste.team ?? [])),
    titelFehlt: 'Bitte angeben, von wem der Plan ist (Titel oben).',
    gespeichert: (d) => `Bewässerungsplan «${d.titel}» gespeichert – Excel liegt beim Kunden`,
  });
  const uebersichten = useChecklisten(nr, { laden: neu, frisch: true });
  const [prog, setProg] = useState('A');
  const [anzahl, setAnzahl] = useState('');
  const [uebernimmt, setUebernimmt] = useState(false);

  const setProgramm = (p, patch) => aendern((d) => { d.programme[p] = { ...d.programme[p], ...patch }; return d; });
  const tagUmschalten = (t) => aendern((d) => {
    const p = d.programme[prog];
    p.tage = p.modus === 'tage' ? { ...p.tage, [t]: !p.tage[t] } : { ...Object.fromEntries(TAGE.map((x) => [x, false])), [t]: true };
    p.modus = 'tage';
    p.intervall = '';
    return d;
  });
  const setStart = (i, wert) => aendern((d) => { const s = [...d.programme[prog].start]; s[i] = wert; d.programme[prog].start = s; return d; });
  const setZone = (i, patch) => aendern((d) => { d.zonen[i] = { ...d.zonen[i], ...patch }; return d; });
  const setLaufzeit = (i, wert) => aendern((d) => { d.zonen[i] = { ...d.zonen[i], laufzeit: { ...d.zonen[i].laufzeit, [prog]: wert } }; return d; });
  const fuerAlle = (wert) => aendern((d) => { d.zonen = d.zonen.map((z) => ({ ...z, laufzeit: { ...z.laufzeit, [prog]: wert } })); return d; });
  const zonenAnlegen = (n) => aendern((d) => {
    const ziel = Math.min(Math.max(n, 1), MAX_ZONEN);
    while (d.zonen.length < ziel) d.zonen.push(leereZone(d.zonen.length + 1));
    return d;
  });
  const zoneWeg = () => aendern((d) => { d.zonen.pop(); return d; });

  /** Zonen samt Bezeichnungen aus einer Übersichtsliste übernehmen (ein Tipp). */
  const zonenAus = async (liste) => {
    setUebernimmt(true);
    try {
      const r = await api(`/auftraege/${encodeURIComponent(nr)}/checklisten/${encodeURIComponent(liste.dateiId)}`);
      const zonen = r.daten.zonen.map((z, i) => leereZone(i + 1, z.bezeichnung));
      if (!zonen.length) { setFehler(`«${liste.titel}» hat noch keine Zonen.`); return; }
      aendern((d) => { d.zonen = zonen; if (!d.titel) d.titel = liste.titel; return d; });
      toast({ text: `${zonen.length} Zonen aus «${liste.titel}» übernommen` });
    } catch (err) {
      setFehler(err.message);
    } finally {
      setUebernimmt(false);
    }
  };

  const abschnitte = useAbschnitte(['programme', 'zonen', 'geraet']);

  const zonenInProg = useMemo(() => (daten ? Object.fromEntries(PROGRAMME.map((p) => [p, daten.zonen.filter((z) => z.laufzeit[p]).length])) : {}), [daten]);

  if (q.isLoading && !daten) return <div className="narrow"><div className="skeleton" style={{ height: 120 }} /><div className="skeleton" style={{ height: 320 }} /></div>;
  if (!daten) {
    return (
      <div className="narrow">
        <div className="empty" role="alert">
          {q.error?.message ?? 'Bewässerungsplan nicht verfügbar.'}
          <button className="btn" onClick={() => q.refetch()}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  const datei = server?.datei;
  const p = daten.programme[prog];
  const vorlagen = uebersichten.data?.listen ?? [];
  return (
    <div className="narrow">
      <div className="between" style={{ gap: 8 }}>
        <Link to={`${base}/plaene`} className="linkbtn" style={{ fontSize: 13, minHeight: 40, display: 'inline-flex', alignItems: 'center' }}>‹ Alle Bewässerungspläne</Link>
        <Stand abfragen={[q]} />
      </div>
      <section className="card-dark" style={{ padding: 18, gap: 8 }}>
        <span className="eyebrow on-dark-muted">Bewässerungszeitplan von</span>
        <TitelFeld wert={daten.titel} onChange={(titel) => aendern((d) => ({ ...d, titel }))} placeholder="z. B. Ausfahrt-Kreisel Nord" max={120} autoFocus={neu && !daten.titel} label="Bewässerungszeitplan von" groesse={22} />
        {datei && <ExcelKnopf datei={datei} />}
      </section>
      <section className="card" style={{ gap: 10 }}>
        <div className="stack" style={{ gap: 4 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Ausgefüllt von</span>
          <TeamWahl wert={daten.ausgefuelltVon} team={team} onChange={(ausgefuelltVon) => aendern((d) => ({ ...d, ausgefuelltVon }))} />
        </div>
        <label className="stack" style={{ gap: 2, maxWidth: 190 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Datum</span>
          <input type="date" className="input" style={{ height: 48, padding: '0 8px', fontSize: 15 }} value={daten.datum} onChange={(e) => { const datum = e.target.value; aendern((d) => ({ ...d, datum })); }} />
        </label>
      </section>

      {veraltet && (
        <div className="notice err" role="alert">
          <Icon name="warn" size={18} />
          <span>Der Plan wurde inzwischen geändert (in der App oder in Excel). Deine Eingaben sind noch hier, lassen sich aber nicht speichern.
            <button className="linkbtn" onClick={verwerfen} style={{ marginLeft: 6 }}>Aktuellen Stand laden</button></span>
        </div>
      )}

      <AbschnittsLeiste aktiv={abschnitte.aktiv} onSpringe={abschnitte.springe} eintraege={[
        { key: 'programme', titel: 'Programme', info: PROGRAMME.filter((x) => programmInfo(daten.programme[x])).join(' ') || 'einstellen' },
        { key: 'zonen', titel: 'Zonen', info: `${daten.zonen.length} Zonen` },
        { key: 'geraet', titel: 'Steuergerät', info: daten.steuergeraet.typ || 'Typ, Ort' },
      ]} />

      {/* ── Programme ── */}
      <div ref={abschnitte.ref('programme')} />
      <Abschnittstitel titel="Programme" info="Tage und Startzeiten pro Programm" />
      <ProgrammWahl prog={prog} setProg={setProg} daten={daten} zonenInProg={zonenInProg} />
      <section className="card" style={{ gap: 12 }}>
        <div className="stack" style={{ gap: 6 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Tage · Programm {prog}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
            {TAGE.map((t) => {
              const an = p.modus === 'tage' && p.tage[t];
              return (
                <button key={t} type="button" className={`chip${an ? ' on' : ''}`} aria-pressed={an} onClick={() => tagUmschalten(t)}
                  style={{ height: 48, padding: 0, fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: p.modus === 'tage' ? 1 : 0.45 }}>{TAG_NAMEN[t]}</button>
              );
            })}
          </div>
          <div className="wrapchips">
            <button type="button" className={`chip accent${p.modus === 'tage' && TAGE.every((t) => p.tage[t]) ? ' on' : ''}`} style={{ height: 48, padding: '0 12px', fontSize: 12 }}
              onClick={() => setProgramm(prog, { modus: 'tage', intervall: '', tage: Object.fromEntries(TAGE.map((t) => [t, !(p.modus === 'tage' && TAGE.every((x) => p.tage[x]))])) })}>Täglich</button>
            {[['gerade', 'Gerade Tage'], ['ungerade', 'Ungerade Tage'], ['intervall', 'Intervall …']].map(([m, label]) => (
              <button key={m} type="button" className={`chip accent${p.modus === m ? ' on' : ''}`} style={{ height: 48, padding: '0 12px', fontSize: 12 }}
                onClick={() => setProgramm(prog, p.modus === m ? { modus: 'tage' } : { modus: m, intervall: m === 'intervall' ? p.intervall : '' })}>{label}</button>
            ))}
          </div>
          {p.modus === 'intervall' && (
            <input className="input" style={{ height: 48 }} value={p.intervall} onChange={(e) => setProgramm(prog, { intervall: e.target.value })} placeholder="z. B. alle 3 Tage" maxLength={30} autoFocus />
          )}
        </div>
        <div className="stack" style={{ gap: 6 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Startzeiten · Programm {prog}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
            {[0, 1, 2, 3].map((i) => (
              <label key={i} className="row" style={{ gap: 6 }}>
                <span className="sub tn" style={{ width: 14, fontWeight: 700 }}>{i + 1}</span>
                <input type="time" className="input" style={{ height: 48, padding: '0 8px', fontSize: 15, flex: 1, minWidth: 0 }} value={p.start[i]} onChange={(e) => setStart(i, e.target.value)} aria-label={`Startzeit ${i + 1} Programm ${prog}`} />
              </label>
            ))}
          </div>
        </div>
      </section>

      {/* ── Zonen & Laufzeiten ── */}
      <div ref={abschnitte.ref('zonen')} style={{ height: 4 }} />
      <Abschnittstitel titel="Zonen & Laufzeiten" info={`${daten.zonen.length} Zonen · ${zonenInProg[prog] ?? 0} in Programm ${prog}`} />
      {daten.zonen.length === 0 ? (
        <section className="card">
          {vorlagen.length > 0 && (
            <div className="stack" style={{ gap: 6 }}>
              <span style={{ fontWeight: 800 }}>Zonen übernehmen</span>
              <div className="wrapchips">
                {vorlagen.map((l) => (
                  <button key={l.dateiId} type="button" className="chip accent" style={{ height: 48 }} onClick={() => zonenAus(l)} disabled={uebernimmt}>aus «{l.titel}»</button>
                ))}
              </div>
              <span className="sub" style={{ fontSize: 12 }}>Nimmt Anzahl und Bezeichnungen der Zonen aus der Übersichtsliste.</span>
            </div>
          )}
          <span style={{ fontWeight: 800 }}>{vorlagen.length ? 'Oder Anzahl Zonen' : 'Wie viele Zonen?'}</span>
          <div className="wrapchips">
            {[2, 3, 4, 5, 6, 8, 10, 12].map((n) => <button key={n} type="button" className="chip tn" style={{ minWidth: 52 }} onClick={() => zonenAnlegen(n)}>{n}</button>)}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <input className="input" type="number" inputMode="numeric" min={1} max={MAX_ZONEN} value={anzahl} onChange={(e) => setAnzahl(e.target.value)} placeholder="andere Anzahl" style={{ maxWidth: 160 }} />
            <button type="button" className="btn secondary" onClick={() => zonenAnlegen(Number(anzahl) || 1)} disabled={!Number(anzahl)}>Anlegen</button>
          </div>
        </section>
      ) : (
        <>
          <ProgrammWahl prog={prog} setProg={setProg} daten={daten} zonenInProg={zonenInProg} />
          <section className="card" style={{ gap: 0, padding: '6px 14px' }}>
            <div className="row" style={{ gap: 8, padding: '8px 0 10px' }}>
              <span style={{ flex: 1, fontWeight: 800, fontSize: 14 }}>Für alle Zonen</span>
              <LaufzeitFeld wert="" onChange={(w) => w && fuerAlle(w)} label={`Laufzeit für alle Zonen in Programm ${prog}`} platzhalter="Laufzeit …" />
            </div>
            {daten.zonen.map((z, i) => (
              <div key={z.nr} className="row" style={{ gap: 8, padding: '8px 0', borderTop: '1px solid var(--line-2)' }}>
                <span className="tn" style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800,
                  background: z.laufzeit[prog] ? 'var(--accent)' : 'var(--bg)', color: z.laufzeit[prog] ? '#fff' : 'var(--ink)' }}>{z.nr}</span>
                <input className="input" style={{ height: 48, flex: 1, minWidth: 0 }} value={z.bezeichnung} onChange={(e) => setZone(i, { bezeichnung: e.target.value })}
                  placeholder="Bezeichnung" maxLength={80} aria-label={`Bezeichnung Zone ${z.nr}`} />
                <LaufzeitFeld wert={z.laufzeit[prog]} onChange={(w) => setLaufzeit(i, w)} label={`Laufzeit Zone ${z.nr} Programm ${prog}`} platzhalter="–" />
              </div>
            ))}
          </section>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn secondary" style={{ flex: 1 }} onClick={() => zonenAnlegen(daten.zonen.length + 1)} disabled={daten.zonen.length >= MAX_ZONEN}>
              <Icon name="plus" size={18} stroke={2.4} />Zone {daten.zonen.length + 1}
            </button>
            <button type="button" className="btn secondary" onClick={zoneWeg} aria-label={`Zone ${daten.zonen.length} entfernen`}><Icon name="minus" size={18} stroke={2.4} /></button>
          </div>
          <p className="sub" style={{ fontSize: 12, margin: '0 4px' }}>Zonen ohne Laufzeit laufen in diesem Programm nicht mit (z. B. Rasen in A, Töpfe in B).</p>
        </>
      )}

      {/* ── Steuergerät ── */}
      <div ref={abschnitte.ref('geraet')} style={{ height: 4 }} />
      <Abschnittstitel titel="Steuergerät" info="Typ, Standort, Regensensor" />
      <section className="card" style={{ gap: 12 }}>
        <div className="stack" style={{ gap: 6 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Typ</span>
          <div className="wrapchips">
            {[...STEUERGERAETE, ...(daten.steuergeraet.typ && !STEUERGERAETE.includes(daten.steuergeraet.typ) ? [daten.steuergeraet.typ] : [])].map((t) => (
              <button key={t} type="button" className={`chip accent${daten.steuergeraet.typ === t ? ' on' : ''}`} style={{ height: 48, padding: '0 12px', fontSize: 12 }}
                onClick={() => aendern((d) => ({ ...d, steuergeraet: { ...d.steuergeraet, typ: d.steuergeraet.typ === t ? '' : t } }))}>{t}</button>
            ))}
          </div>
          <input className="input" style={{ height: 48 }} value={daten.steuergeraet.typ} onChange={(e) => { const typ = e.target.value; aendern((d) => ({ ...d, steuergeraet: { ...d.steuergeraet, typ } })); }} placeholder="oder Typ eintippen" maxLength={60} aria-label="Steuergerät Typ" />
        </div>
        <label className="stack" style={{ gap: 2 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Auffindbar im Raum</span>
          <input className="input" style={{ height: 48 }} value={daten.steuergeraet.standort} onChange={(e) => { const standort = e.target.value; aendern((d) => ({ ...d, steuergeraet: { ...d.steuergeraet, standort } })); }} placeholder="z. B. Garage, Technikraum" maxLength={120} />
        </label>
        <div className="stack" style={{ gap: 6 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Regensensor</span>
          <div className="wrapchips">
            {['Ja', 'Nein'].map((w) => (
              <button key={w} type="button" className={`chip${daten.regensensor === w ? ' on' : ''}`} style={{ height: 48, minWidth: 70 }} onClick={() => aendern((d) => ({ ...d, regensensor: d.regensensor === w ? '' : w }))}>{w}</button>
            ))}
          </div>
        </div>
        <label className="stack" style={{ gap: 2 }}>
          <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Sonstiges</span>
          <textarea className="input" rows={3} style={{ minHeight: 70 }} value={daten.sonstiges} onChange={(e) => { const sonstiges = e.target.value; aendern((d) => ({ ...d, sonstiges })); }} maxLength={500} placeholder="z. B. Hauptschieber im Keller" />
        </label>
      </section>

      <SpeichernLeiste entwurf={entwurf} vorgemerkt={vorgemerkt} fehler={fehler} online={online} speichert={speichert} veraltet={veraltet} titelOk={!!daten.titel?.trim()}
        onVerwerfen={verwerfen} onSpeichern={absenden} label="Bewässerungsplan speichern" />
    </div>
  );
}

/** Programm A | B | C umschalten – mit Kurzinfo, was eingestellt ist. */
function ProgrammWahl({ prog, setProg, daten, zonenInProg }) {
  return (
    <div role="tablist" aria-label="Programm" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
      {PROGRAMME.map((x) => {
        const info = programmInfo(daten.programme[x]);
        const an = prog === x;
        return (
          <button key={x} type="button" role="tab" aria-selected={an} onClick={() => setProg(x)}
            style={{ border: `2px solid ${an ? 'var(--accent)' : 'transparent'}`, borderRadius: 14, padding: '8px 8px', textAlign: 'left', cursor: 'pointer', font: 'inherit', background: 'var(--card)', color: 'var(--ink)', minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Programm {x}</span>
            <span className="ellipsis" style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>{info || 'nicht benutzt'}{zonenInProg[x] ? ` · ${zonenInProg[x]} Z.` : ''}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Laufzeit wählen (Liste) oder eintippen («Andere …»). */
function LaufzeitFeld({ wert, onChange, label, platzhalter }) {
  const [eigen, setEigen] = useState(false);
  const optionen = wert && !LAUFZEITEN.includes(wert) ? [wert, ...LAUFZEITEN] : LAUFZEITEN;
  if (eigen) {
    return (
      <input className="input" style={{ height: 48, width: 112, flexShrink: 0 }} autoFocus defaultValue={wert} placeholder="z. B. 35 min" maxLength={20} aria-label={label}
        onBlur={(e) => { onChange(e.target.value.trim()); setEigen(false); }} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
    );
  }
  return (
    <select className="input" style={{ height: 48, width: 112, flexShrink: 0, padding: '0 8px', fontSize: 15, fontWeight: wert ? 700 : 400 }} value={wert} aria-label={label}
      onChange={(e) => { if (e.target.value === '__andere') setEigen(true); else onChange(e.target.value); }}>
      <option value="">{platzhalter}</option>
      {optionen.map((o) => <option key={o} value={o}>{o}</option>)}
      <option value="__andere">Andere …</option>
    </select>
  );
}
